import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { isEqual } from 'lodash-es';
import { ScopedThemeProvider, useIsInModal, useOverlayContext, useStateInRef } from 'seagull';
import { isValidHexColor } from '~/business-logic/colorsLogic';
import { adjustBackgroundForContrast, getBrandColorWithContrast, getDisabledTextColorForBackground, getTextColorForBrandOnBackground, getTextColorsForBackground, getTextColorForTintedBrandBackground, getTextButtonOnSecondaryBgHover, validateAndCorrectGradient } from '~/business-logic/contrastLogic';
import { ErrorText } from '~/constants';
import { useStore, useStoreActions, useStoreStatic } from '~/contexts';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useUploadLogo } from '~/hooks/useUploadLogo';
import { useLocaleKeys } from '~/i18n';
import { handleRequest } from '~/infra/utils';
import { selectors } from '~/store/state-selectors';
import { makeDefaultBranding } from '~/store/store.utils';
const brandingToCSSVar = {
    backgroundGradientStart: '--bg-gradient-color-1',
    backgroundGradientEnd: '--bg-gradient-color-2',
    backgroundStandard: '--bg-standard',
    backgroundLight: '--bg-light',
    buttonPrimary: '--button-primary',
    buttonSecondary: '--button-secondary',
    buttonDisabled: '--button-disabled',
    progressBarComplete: '--stepper-completed',
    progressBarIncomplete: '--stepper-incomplete',
    textStandard: '--text-standard',
    textSecondary: '--text-secondary'
};
/**
 * Build brand CSS variables. When useContrastLogic is true (CLIENT_ONBOARDING_NEW_FLOW),
 * applies WCAG AA contrast: gradient validation, background correction, auto text colors.
 * When false (non-flagged path), uses branding values as-is.
 */ function getBrandCSS(branding, fallbackBranding) {
    let useContrastLogic = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : false;
    var _ret_bggradientcolor1, _ret_bggradientcolor2, _ret_bgstandard, _ret_bglight, _ret_buttonprimary;
    const ret = {};
    Object.entries(brandingToCSSVar).forEach((param)=>{
        let [keyOfBranding, keyOfBrandCSS] = param;
        if (useContrastLogic && (keyOfBranding === 'textStandard' || keyOfBranding === 'textSecondary')) return;
        ret[keyOfBrandCSS] = !fallbackBranding || isValidHexColor(branding[keyOfBranding]) ? branding[keyOfBranding] : fallbackBranding[keyOfBranding];
    });
    const gradientStart = (_ret_bggradientcolor1 = ret['--bg-gradient-color-1']) !== null && _ret_bggradientcolor1 !== void 0 ? _ret_bggradientcolor1 : branding.backgroundGradientStart;
    const gradientEnd = (_ret_bggradientcolor2 = ret['--bg-gradient-color-2']) !== null && _ret_bggradientcolor2 !== void 0 ? _ret_bggradientcolor2 : branding.backgroundGradientEnd;
    const bgStandard = (_ret_bgstandard = ret['--bg-standard']) !== null && _ret_bgstandard !== void 0 ? _ret_bgstandard : branding.backgroundStandard;
    const bgLight = (_ret_bglight = ret['--bg-light']) !== null && _ret_bglight !== void 0 ? _ret_bglight : branding.backgroundLight;
    if (useContrastLogic) {
        var _ret_buttonprimary1, _ret_bgstandard1;
        // V2: Gradient validation and correction for WCAG AA
        const gradientResult = validateAndCorrectGradient(gradientStart, gradientEnd);
        ret['--bg-gradient-color-1'] = gradientResult.startHex;
        ret['--bg-gradient-color-2'] = gradientResult.endHex;
        // Text on primary background: computed from Custom color 1 (first gradient stop) only, not the full gradient
        const primaryBgForText = gradientResult.startHex;
        const primaryTextColors = getTextColorsForBackground(primaryBgForText);
        ret['--text-on-primary-bg'] = primaryTextColors.primary;
        ret['--text-on-primary-bg-secondary'] = primaryTextColors.secondary;
        // Links, icons, accents on primary: prefer brand (or adjusted brand) when contrast OK
        ret['--accent-on-primary-bg'] = getBrandColorWithContrast(primaryBgForText, (_ret_buttonprimary1 = ret['--button-primary']) !== null && _ret_buttonprimary1 !== void 0 ? _ret_buttonprimary1 : branding.buttonPrimary);
        if (isValidHexColor(bgLight)) {
            ret['--bg-light'] = adjustBackgroundForContrast(bgLight);
        }
        // Secondary background (cards, sidebar): adjust for WCAG AA contrast with text
        if (isValidHexColor(bgStandard)) {
            ret['--bg-standard'] = adjustBackgroundForContrast(bgStandard);
        }
        // Text on secondary background (cards): for content inside cards
        const correctedBgStandard = (_ret_bgstandard1 = ret['--bg-standard']) !== null && _ret_bgstandard1 !== void 0 ? _ret_bgstandard1 : isValidHexColor(bgStandard) ? bgStandard : null;
        const bgForText = correctedBgStandard && isValidHexColor(correctedBgStandard) ? correctedBgStandard : null;
        if (bgForText) {
            const textColors = getTextColorsForBackground(bgForText);
            ret['--text-standard'] = textColors.primary;
            ret['--text-secondary'] = textColors.secondary;
            ret['--text-on-standard-bg'] = textColors.primary;
            ret['--text-on-standard-bg-secondary'] = textColors.secondary;
            ret['--text-on-standard-bg-disabled'] = getDisabledTextColorForBackground(bgForText);
        }
    } else {
        // Non-flagged path: use branding as-is; mirror text vars for consumers that expect them
        const textStandard = ret['--text-standard'];
        const textSecondary = ret['--text-secondary'];
        if (textStandard) {
            ret['--text-on-standard-bg'] = textStandard;
            ret['--text-on-primary-bg'] = textStandard;
        }
        if (textSecondary) {
            ret['--text-on-standard-bg-secondary'] = textSecondary;
            ret['--text-on-primary-bg-secondary'] = textSecondary;
        }
    }
    const buttonPrimary = (_ret_buttonprimary = ret['--button-primary']) !== null && _ret_buttonprimary !== void 0 ? _ret_buttonprimary : branding.buttonPrimary;
    if (useContrastLogic) {
        var _ret_buttonsecondary, _ret_bgstandard2;
        // Badges and buttons with tinted bg (color-mix 15% brand): use contrast-safe text color
        ret['--text-on-tinted-brand-bg'] = getTextColorForTintedBrandBackground(buttonPrimary);
        ret['--badge-primary-bg'] = 'transparent';
        ret['--badge-primary-border'] = buttonPrimary;
        // Primary filled button: text must contrast with button background (brand color)
        ret['--button-text-on-filled'] = getTextColorsForBackground(buttonPrimary).primary;
        // Primary outline opaque button (e.g. Get started): bg = buttonSecondary, text = brand
        const buttonSecondary = (_ret_buttonsecondary = ret['--button-secondary']) !== null && _ret_buttonsecondary !== void 0 ? _ret_buttonsecondary : branding.buttonSecondary;
        if (isValidHexColor(buttonSecondary)) {
            ret['--button-text-on-outline-opaque'] = getTextColorForBrandOnBackground(buttonSecondary, buttonPrimary);
        }
        // Text buttons on secondary/card bg, sidebar selected step, badges: prefer brand (or adjusted brand) when contrast OK
        const correctedBgStandardForContrast = (_ret_bgstandard2 = ret['--bg-standard']) !== null && _ret_bgstandard2 !== void 0 ? _ret_bgstandard2 : isValidHexColor(bgStandard) ? bgStandard : null;
        if (correctedBgStandardForContrast) {
            const textButtonColor = getBrandColorWithContrast(correctedBgStandardForContrast, buttonPrimary);
            ret['--text-button-on-secondary-bg'] = textButtonColor;
            ret['--text-button-on-secondary-bg-hover'] = getTextButtonOnSecondaryBgHover(textButtonColor, buttonPrimary);
            ret['--step-selected-color'] = textButtonColor;
            ret['--badge-primary-color'] = textButtonColor;
        } else {
            ret['--badge-primary-color'] = ret['--text-on-tinted-brand-bg'];
        }
    } else {
        ret['--badge-primary-bg'] = 'transparent';
        ret['--badge-primary-border'] = buttonPrimary;
        ret['--badge-primary-color'] = buttonPrimary;
    }
    return ret;
}
export function OverrideBranding(param) {
    let { children, className, testId } = param;
    const { CLIENT_ONBOARDING_NEW_FLOW = false } = useFeatureFlags();
    const brandCSS = useMemo(()=>getBrandCSS(makeDefaultBranding(), undefined, CLIENT_ONBOARDING_NEW_FLOW), [
        CLIENT_ONBOARDING_NEW_FLOW
    ]);
    return /*#__PURE__*/ _jsx(ScopedThemeProvider, {
        className: className,
        theme: brandCSS,
        testId: testId,
        children: children
    });
}
const CobBrandContext = /*#__PURE__*/ createContext({
    branding: makeDefaultBranding(),
    logoImage: '',
    isDirty: false,
    errors: new Set(),
    isValid: true,
    setBranding: ()=>{},
    revertToDefault: ()=>{},
    revertToInitial: ()=>{},
    saveLogoAndBranding: async ()=>{},
    setLogo: ()=>{}
});
export function CobBrandingProvider(props) {
    const getProps = useStateInRef(props);
    const { CLIENT_ONBOARDING_NEW_FLOW = false } = useFeatureFlags();
    const { startLoader, stopLoader, showErrorToast, showSuccessToast } = useOverlayContext();
    const { t: commonTexts } = useLocaleKeys('common');
    const businessBranding = useStore(selectors.user.branding);
    const worksAt = useStore(selectors.user.business);
    const { getState } = useStoreStatic();
    const { api, setBranding: setBusinessBranding, logBI } = useStoreActions();
    const { uploadLogo } = useUploadLogo(worksAt);
    const getUploadLogo = useStateInRef(uploadLogo);
    const [logo, setLogo] = useState();
    const businessLogoUrl = worksAt === null || worksAt === void 0 ? void 0 : worksAt.logoUrl;
    const initialBranding = useMemo(()=>props.initialBranding || businessBranding, [
        businessBranding,
        props.initialBranding
    ]);
    const [branding, setBranding] = useState(initialBranding);
    const value = useMemo(()=>{
        const logoUrl = logo === null || logo === void 0 ? void 0 : logo.asDataURL;
        const isBrandingDirty = !isEqual(branding, initialBranding);
        const isLogoDirty = !!logoUrl && logoUrl !== businessLogoUrl;
        const isDirty = isBrandingDirty || isLogoDirty;
        const errors = new Set(Object.entries(branding).filter((param)=>{
            let [, value] = param;
            return !isValidHexColor(value);
        }).map((param)=>{
            let [key] = param;
            return key;
        }));
        const isValid = errors.size === 0;
        return {
            branding,
            logoImage: logoUrl || businessLogoUrl || '',
            isDirty,
            errors,
            isValid,
            setLogo: (logo)=>{
                var _getProps_onLogoChange, _getProps;
                setLogo(logo);
                (_getProps_onLogoChange = (_getProps = getProps()).onLogoChange) === null || _getProps_onLogoChange === void 0 ? void 0 : _getProps_onLogoChange.call(_getProps, logo.asDataURL);
            },
            setBranding,
            revertToDefault: ()=>{
                logBI(11218);
                setBranding(makeDefaultBranding());
            },
            revertToInitial: ()=>{
                setBranding(initialBranding);
                setLogo(undefined);
            },
            saveLogoAndBranding: async ()=>{
                const businessId = selectors.user.businessId(getState());
                if (!isValid || !isDirty || !businessId) {
                    return;
                }
                logBI(11219);
                startLoader();
                const [updateBranding, { success: uploadLogoSuccess }] = await Promise.all([
                    isBrandingDirty ? handleRequest(api.user.updateBranding(businessId, branding)).then((result)=>{
                        if (!result.success) {
                            showErrorToast(ErrorText.UnexpectedErrorTryAgain, 'update-branding-error');
                        } else {
                            setBusinessBranding(branding);
                        }
                        return result;
                    }) : handleRequest(Promise.resolve()),
                    isLogoDirty ? getUploadLogo()(logo) : Promise.resolve({
                        success: true
                    })
                ]);
                if (uploadLogoSuccess && updateBranding.success) {
                    showSuccessToast(commonTexts.toasts.savedSuccessfully(), 'update-branding-success');
                }
                stopLoader();
            }
        };
    }, [
        branding,
        initialBranding,
        logo,
        businessLogoUrl
    ]);
    const brandCSS = useMemo(()=>getBrandCSS(branding, initialBranding, CLIENT_ONBOARDING_NEW_FLOW), [
        branding,
        initialBranding,
        CLIENT_ONBOARDING_NEW_FLOW
    ]);
    const isInModal = useIsInModal();
    useEffect(()=>{
        if (!CLIENT_ONBOARDING_NEW_FLOW || isInModal) return;
        const buttonPrimary = brandCSS['--button-primary'];
        if (!buttonPrimary) return;
        document.body.style.setProperty('--button-primary', buttonPrimary);
        document.body.style.setProperty('--toast-info-bg', "color-mix(in srgb, ".concat(buttonPrimary, " 15%, white)"));
        document.body.style.setProperty('--toast-info-icon-color', buttonPrimary);
        return ()=>{
            document.body.style.removeProperty('--button-primary');
            document.body.style.removeProperty('--toast-info-bg');
            document.body.style.removeProperty('--toast-info-icon-color');
        };
    }, [
        CLIENT_ONBOARDING_NEW_FLOW,
        brandCSS,
        isInModal
    ]);
    return /*#__PURE__*/ _jsx(CobBrandContext.Provider, {
        value: value,
        children: /*#__PURE__*/ _jsx(ScopedThemeProvider, {
            maxHeight: true,
            theme: brandCSS,
            children: props.children
        })
    });
}
export const useCobBranding = ()=>useContext(CobBrandContext);
